import React, {useState, useEffect, useCallback} from 'react';
import './ElectionResultsTable.css';
import {openPopup} from "../config/windowUtils";

const ElectionResultsTable = ({
  endpoint,
  showParty2020 = false,
  showViewLink = false,
  dataKey = 'state', // Default to 'state' for backward compatibility
  fallbackData = [],
  showUpdateTime = false,
  showHung = false
}) => {
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updatedTime, setUpdatedTime] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const now = new Date();
      const timestamp = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;

      const response = await fetch(`${endpoint}?ver=${timestamp}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const data = await response.json();
      const responseData = data[dataKey] || [];

      // Transform the data to match the expected format
      const transformedData = Object.entries(responseData).map(([key, value]) => {
        return {
          name: value.name?.toUpperCase() || key.toUpperCase(),
          total: value.total || 0,
          ldf: value.ldf || 0,
          udf: value.udf || 0,
          nda: value.nda || 0,
          hung: value.hung || 0,
          oth: value.oth || 0,
          party_20_20: value.party_20_20 || 0
        };
      });

      setTableData(transformedData);
      setUpdatedTime(new Date().toLocaleTimeString('en-IN', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }));
    } catch (err) {
      console.error('Error fetching data:', err);
      // Use fallback data if provided
      if (fallbackData.length > 0) {
        setTableData(fallbackData);
      }
    } finally {
      setIsLoading(false);
    }
  }, [endpoint, dataKey, fallbackData]);

  const getCellClassName = (party, item, leadingParty) => {
    const isSpecialItem = dataKey === 'district' &&
      (item.name === 'CORPORATION' || item.name === 'DIST. PANCHAYATH');

    if (!isSpecialItem) {
      return '';
    }

    const partyValues = ['ldf', 'udf', 'nda', 'oth'].map(p => ({
      party: p,
      value: parseInt(item[p] || 0, 10)
    }));

    const maxValue = Math.max(...partyValues.map(p => p.value));

    // Count how many parties have the maximum value
    const maxValueParties = partyValues.filter(p => p.value === maxValue);

    if (leadingParty !== party || maxValueParties.length > 1) {
      return 'td-1-2';
    }

    return `lead ${party} flipping-text-Y flipping-text`;
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (isLoading && tableData.length === 0) {
    return null
  }

  return (
    <>
      <table className="election-table">
        <thead>
        <tr>
          <th>LEAD / result</th>
          <th className="ldf">LDF</th>
          <th className="udf">UDF</th>
          <th className="nda">NDA</th>
          {showHung && <th className="hung">HUNG</th>}
          {!showViewLink ? <th className="oth">OTH</th> : <th> </th>}
        </tr>
        </thead>
        <tbody>
        {tableData.map((item, index) => {
          const parties = [
            { name: 'ldf', value: item.ldf },
            { name: 'udf', value: item.udf },
            { name: 'nda', value: item.nda },
            { name: 'hung', value: item.hung },
            { name: 'oth', value: item.oth }
          ];

          // Sort by value in descending order
          const sortedParties = [...parties].sort((a, b) => b.value - a.value);
          const leadingParty = sortedParties[0].value > 0 ? sortedParties[0].name : null;

          const getDistrictFromEndpoint = (url) => {
            if (dataKey !== 'district') return '';

            try {
              const urlObj = new URL(url);
              const pathParts = urlObj.pathname.split('/');
              const lastPart = pathParts[pathParts.length - 1];
              return url.includes('mathrubhumi.com')
                ? lastPart.replace('.json', '')
                : lastPart;
            } catch (e) {
              console.error('Error extracting district from URL:', e);
              return '';
            }
          };

          let localBody = item.name
              .trim()
              .toLowerCase()
              .replace(/\s+/g, '')
              .replace(/\./g, '');

          const currentDistrict = (dataKey === 'district' && localBody !== 'corporation' && localBody !== 'distpanchayath')
            ? getDistrictFromEndpoint(endpoint)
            : '';

          localBody = currentDistrict ? `${currentDistrict}-${localBody}` : localBody;

          return (
            <tr key={index}>
              <td>{item.name.replace('PANCHAYATH', 'PANCHAYAT')} ({item.total})</td>
              <td className={getCellClassName('ldf', item, leadingParty)}>{item.ldf}</td>
              <td className={getCellClassName('udf', item, leadingParty)}>{item.udf}</td>
              <td className={getCellClassName('nda', item, leadingParty)}>{item.nda}</td>
              {showHung && <td className={getCellClassName('hung', item, leadingParty)}>{item.hung}</td>}
              {!showViewLink ? (
                <td className={getCellClassName('oth', item, leadingParty)}>{item.oth}</td>
              ) : (
                <td>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      openPopup(`https://results.mathrubhumi.com/result/panchayat-2025/full-result.html?localbody=${localBody}`, 800, 800);
                    }}
                    className="view-link"
                  >
                    View
                  </button>
                </td>
              )}
            </tr>
          );
        })}
        </tbody>
      </table>
      <div className="footer">
        {showParty2020 && tableData.find(item =>
          item.name === 'PANCHAYATH' && item.party_20_20 > 0
        ) && (
          <div className="party_20_20">
            <span>PANCHAYAT 20/20</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="6"
              height="7"
              viewBox="0 0 6 7"
              fill="none"
              aria-hidden="true"
            >
              <path d="M6 3.46411L-3.26266e-07 6.92821L-2.34249e-08 9.51289e-06L6 3.46411Z" fill="#787878"/>
            </svg>
            <span className="party_20_20_count">
                {tableData.find(item => item.name === 'PANCHAYATH')?.party_20_20 || 0}
              </span>
          </div>
        )}
        <div className="update-time">
          {showUpdateTime && updatedTime ? (
            <div style={{display: 'flex', alignItems: 'center', gap: '9px'}}>
              {`Updated @ ${updatedTime} IST`}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  if (window.self !== window.top) {
                    window.location.reload();
                  } else {
                    window.location.reload();
                  }
                }}
                title="Refresh data"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px'
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M11.2278 2.55342L9.1875 4.59375H13.125V0.65625L11.2278 2.55342ZM11.2278 2.55342C10.1489 1.38672 8.60508 0.65625 6.89063 0.65625C3.85051 0.65625 1.34687 2.95316 1.02042 5.90625M2.55342 10.5716L0.65625 12.4688V8.53125H4.59375L2.55342 10.5716ZM2.55342 10.5716C3.63237 11.7383 5.17619 12.4688 6.89063 12.4688C9.93077 12.4688 12.4344 10.1719 12.7608 7.21875"
                    stroke="rgba(0, 0, 0, 0.53)"
                    strokeWidth="1.3125"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          ) : dataKey === 'district' ? (
            'Corp/Dst.Pan divisions displayed'
          ) : null}
        </div>
      </div>
    </>
  );
};

export default ElectionResultsTable;